import React from 'react';
import Yup from '~/util/yup';
import { editDiagnosesOptions, getDiagnosesOptions, SymptomControlRatingEnum, SymptomControlRatingOptions } from '@generated';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { Button } from '~/shared/components/buttons/Button';
import { SubmitButton } from '~/shared/components/buttons/SubmitButton';
import { IcdDiagnosisAutocomplete } from '~/libraries/icddiagnoses/IcdDiagnosisAutocomplete';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { DatePickerFormField } from '~/shared/forms/controls/fields/date-fields/DatePickerFormField';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import { FormModal } from '~/shared/forms/FormModal';
import type { FormHelpers } from '~/shared/forms/types';
import type { DiagnosisDraft } from '../types';
import { EditDiagnosesTable } from './EditDiagnosesTable';

const symptomControlRatingLabel = (id: number) => SymptomControlRatingOptions.find(o => o.id === Number(id))?.label ?? '';

const initialFormValues = {
	icdDiagnosisId: null,
	validPdgmDiagnosis: false,
	code: null,
	description: null,
	symptomControlRatingId: null,
	onsetDate: null,
	notes: null
};

export function PatientDiagnosesFormModal({ certificationPeriodId, closeModal }: { certificationPeriodId: string; closeModal: () => void }) {
	const { data: patientDiagnoses } = useSuspenseQuery(getDiagnosesOptions(certificationPeriodId));

	const diagnoses = patientDiagnoses.current ?? patientDiagnoses.pending;
	const [drafts, setDrafts] = React.useState<DiagnosisDraft[]>(() =>
		(diagnoses?.diagnoses ?? [])
			.slice()
			.sort((a, b) => a.order - b.order)
			.map((d, index) => ({
				key: d.icdDiagnosisId,
				icdDiagnosisId: d.icdDiagnosisId,
				code: d.icdDiagnosis.code,
				description: d.icdDiagnosis.description,
				validPdgmDiagnosis: d.icdDiagnosis.validPdgmDiagnosis,
				order: index + 1,
				symptomControlRatingId: d.symptomControlRatingId,
				symptomControlRatingLabel: d.symptomControlRatingLabel,
				notes: d.notes ?? null,
				onsetDate: d.onsetDate ?? null
			}))
	);
	const [currentView, setCurrentView] = React.useState<'list' | 'form'>('list');
	const [editingKey, setEditingKey] = React.useState<string | null>(null);
	const [hasChanges, setHasChanges] = React.useState(false);

	if (!diagnoses) {
		return null;
	}

	const editingDraft = editingKey ? drafts.find(d => d.key === editingKey) : undefined;
	const isPrimary = editingDraft ? editingDraft.order === 1 : drafts.length === 0;
	const primaryPdgmInvalid = drafts.length > 0 && !drafts[0].validPdgmDiagnosis;

	const validationSchema =
		currentView === 'form'
			? Yup.object().shape({
					icdDiagnosisId: Yup.string()
						.label('Diagnosis')
						.nullable()
						.required()
						.test(
							'unique',
							'This diagnosis is already on the list.',
							value => !value || editingKey !== null || !drafts.some(d => d.icdDiagnosisId === value)
						)
						.test(
							'pdgm-primary',
							'Primary Diagnosis must be PDGM compliant.',
							(value, ctx) => !value || editingKey !== null || drafts.length > 0 || !!ctx.parent.validPdgmDiagnosis
						)
						.test('symptomatic', 'Primary diagnoses cannot be asymptomatic', value => {
							if (!value) return true;
							const isPrimary = editingKey ? drafts.find(d => d.key === editingKey)?.order === 1 : drafts.length === 0;
							return !isPrimary || value !== SymptomControlRatingEnum.Asymptomatic.toString();
						}),
					validPdgmDiagnosis: Yup.bool().label('Valid PDGM Diagnosis'),
					symptomControlRatingId: Yup.string().label('Symptom Control Rating').nullable().required(),
					onsetDate: Yup.date().label('Onset Date').nullable(),
					notes: Yup.string().label('Notes').nullable()
			  })
			: undefined;

	const applyDrafts = (next: DiagnosisDraft[]) => {
		setDrafts(next.map((d, index) => ({ ...d, order: index + 1 })));
		setHasChanges(true);
	};

	const handleSorted = (ids: (string | number)[]) => {
		applyDrafts(ids.map(id => drafts.find(d => d.key === id)).filter((d): d is DiagnosisDraft => !!d));
	};

	const openAdd = (formHelpers: FormHelpers) => {
		formHelpers.resetForm({ values: initialFormValues });
		setEditingKey(null);
		setCurrentView('form');
	};

	const openEdit = (d: DiagnosisDraft, formHelpers: FormHelpers) => {
		formHelpers.resetForm({
			values: {
				icdDiagnosisId: d.icdDiagnosisId,
				validPdgmDiagnosis: d.validPdgmDiagnosis,
				code: d.code,
				description: d.description,
				symptomControlRatingId: d.symptomControlRatingId,
				onsetDate: d.onsetDate,
				notes: d.notes
			}
		});
		setEditingKey(d.key);
		setCurrentView('form');
	};

	const backToList = (formHelpers: FormHelpers) => {
		formHelpers.resetForm({ values: initialFormValues });
		setEditingKey(null);
		setCurrentView('list');
	};

	const handleApply = async (formHelpers: FormHelpers) => {
		const errors = await formHelpers.validateForm();
		if (Object.keys(errors).length > 0) {
			await formHelpers.setTouched({ icdDiagnosisId: true, symptomControlRatingId: true });
			return;
		}
		const values = formHelpers.values;
		const scrId = Number(values.symptomControlRatingId);
		if (editingKey) {
			applyDrafts(
				drafts.map(d =>
					d.key === editingKey
						? {
								...d,
								symptomControlRatingId: scrId,
								symptomControlRatingLabel: symptomControlRatingLabel(scrId),
								onsetDate: values.onsetDate,
								notes: values.notes
						  }
						: d
				)
			);
		} else {
			applyDrafts([
				...drafts,
				{
					key: values.icdDiagnosisId,
					icdDiagnosisId: values.icdDiagnosisId,
					code: values.code ?? '',
					description: values.description ?? '',
					validPdgmDiagnosis: !!values.validPdgmDiagnosis,
					order: drafts.length + 1,
					symptomControlRatingId: scrId,
					symptomControlRatingLabel: symptomControlRatingLabel(scrId),
					notes: values.notes,
					onsetDate: values.onsetDate
				}
			]);
		}
		backToList(formHelpers);
	};

	return (
		<FormModal
			title="Diagnoses"
			initialValues={initialFormValues}
			validationSchema={validationSchema}
			saveMutation={{
				...editDiagnosesOptions,
				invalidateQueryKeys: [getDiagnosesOptions(certificationPeriodId).queryKey]
			}}
			onSaveTransform={() => ({
				certificationPeriodId: certificationPeriodId,
				diagnoses: drafts.map(d => ({
					icdDiagnosisId: d.icdDiagnosisId,
					symptomControlRatingId: d.symptomControlRatingId,
					order: d.order,
					notes: d.notes,
					dateOfOnset: d.onsetDate
				}))
			})}
			onSuccess={closeModal}
			closeModal={closeModal}
			submitButton={formHelpers => {
				if (currentView === 'form') {
					return (
						<>
							<Button onClick={() => backToList(formHelpers)} Icon={ChevronLeft} label="Previous" />
							<SubmitButton
								onSubmit={() => handleApply(formHelpers)}
								submitLabel={editingKey ? 'Update' : 'Add'}
								submittingLabel="Loading..."
							/>
						</>
					);
				}
				return (
					<SubmitButton
						onSubmit={formHelpers.submitForm}
						isSubmitting={formHelpers.isSubmitting}
						submitDisabled={!hasChanges || primaryPdgmInvalid}
						submitLabel="Save"
						submittingLabel="Saving"
					/>
				);
			}}
			size="xl"
		>
			{formHelpers => {
				if (currentView === 'form') {
					return (
						<div className="grid grid-cols-1 gap-6">
							<IcdDiagnosisAutocomplete
								name="icdDiagnosisId"
								validPdgmDiagnosisFieldName="validPdgmDiagnosis"
								disableWarning={!isPrimary}
								isReadOnly={!!editingKey}
								onChangePassThrough={selected => {
									formHelpers.setFieldValue('code', selected?.code ?? null);
									formHelpers.setFieldValue('description', selected?.description ?? null);
								}}
							/>
							<SelectFormField name="symptomControlRatingId" options={SymptomControlRatingOptions} />
							<DatePickerFormField name="onsetDate" />
							<TextAreaFormField name="notes" />
						</div>
					);
				}
				return (
					<div className="space-y-4">
						{primaryPdgmInvalid && (
							<p className="text-xs font-medium text-red-600">
								The first diagnosis must be PDGM compliant. Reorder the list or remove the primary diagnosis before saving.
							</p>
						)}
						<div className="flex justify-end">
							<Button actionType="add" intent="neutral" label="Add Diagnosis" onClick={() => openAdd(formHelpers)} />
						</div>
						<EditDiagnosesTable
							rows={drafts}
							onSorted={handleSorted}
							onEdit={d => openEdit(d, formHelpers)}
							onRemove={d => applyDrafts(drafts.filter(x => x.key !== d.key))}
						/>
					</div>
				);
			}}
		</FormModal>
	);
}
